/**
 * 赠礼与福利（Alice 页面 usageReward 的复刻）：账号赠礼金额、用量福利规则、发放记录。
 *
 * 背景：Alice 有三份礼与「Alice 外观」奖励；本仓只有注册礼（邮箱验证后给账号一次性赠送花瓣），
 *   奖励类型保留 bonus（赠送花瓣，Alice 的 token）与 badge（徽章，执行器未接入，只能停用保存），
 *   去掉 outfit（Alice 外观）与 OPC 好友这类产品专属的奖励。
 * 设计意图：版式、列顺序、新建与编辑弹窗与 Alice 一致；规则与发放记录的数据来自控制面，
 *   达标判断由后台任务按水位扫描用量，不在请求链路里，所以页头标签写「达标判断不阻塞推理」。
 *   金额一律用花瓣；规则时间输入用本机时区。
 * 关键约束：保存规则带 expected_revision，409 时重新载入；停发（金额 0）与启用规则的校验由服务端判断，
 *   前端只联动输入框（徽章类型不可启用）；发放记录里 failed 才有「重试」。
 */
import { useEffect, useState } from 'react'
import { campaignApi, type AccountGift, type GrantStatus, type RewardRule, type RewardRuleInput, type RewardType } from '@ap/api-client/admin-campaign'
import { AG_campaign as G, errorTextC } from '@ap/i18n/admin-campaign'
import { intlTag, useI18n } from '@ap/i18n'
import { ConfirmModal, fmtDateTime, navigate, useLoad, useToast } from '@ap/ui'
import { OpsNote, OpsPage } from '../../alice/kit.tsx'
import { AliceModal, ToolSelect, fmtNum, fmtTokens, fromLocalInput, toLocalInput } from '../../alice/kit-campaign.tsx'

interface RuleForm {
  name: string
  modelPattern: string
  threshold: string
  type: RewardType
  ref: string
  startAt: string
  enabled: boolean
}

const EMPTY_RULE: RuleForm = { name: '', modelPattern: '', threshold: '1000000', type: 'bonus', ref: '', startAt: '', enabled: false }

const STATUS_COLOR: Record<GrantStatus, string> = {
  pending: 'var(--warning-text)',
  delivered: 'var(--success-text)',
  owned: 'var(--success-text)',
  failed: 'var(--error-text)',
}

const codeStyle = { fontFamily: 'var(--mono)', fontSize: 11, background: 'var(--bg-muted)', padding: '1px 5px', borderRadius: 3 } as const

export function UsageReward() {
  const { t, locale } = useI18n()
  const tag = intlTag(locale)
  const toast = useToast()
  const gifts = useLoad(() => campaignApi.gifts(), [])
  const rules = useLoad(() => campaignApi.rules(), [])
  const [grantRule, setGrantRule] = useState('')
  const [grantStatus, setGrantStatus] = useState('')
  const grants = useLoad(() => campaignApi.grants(grantRule, grantStatus), [grantRule, grantStatus])

  const [amounts, setAmounts] = useState<Record<string, string>>({})
  const [zeroCode, setZeroCode] = useState<string | null>(null)
  const [editing, setEditing] = useState<RewardRule | 'new' | null>(null)
  const [form, setForm] = useState<RuleForm>(EMPTY_RULE)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (gifts.data) setAmounts(Object.fromEntries(gifts.data.gifts.map((g) => [g.code, String(g.points)])))
  }, [gifts.data])

  const giftLabel = (g: AccountGift) => (g.code in G.reward.giftNames ? t(G.reward.giftNames[g.code].label) : g.label)
  const giftDesc = (g: AccountGift) => (g.code in G.reward.giftNames ? t(G.reward.giftNames[g.code].desc) : g.desc)

  const saveGift = async (code: string, reset: boolean, confirmed = false) => {
    let points: number | null = null
    if (!reset) {
      const raw = (amounts[code] ?? '').trim()
      if (raw === '' || Number.isNaN(Number(raw))) {
        toast.error(t(G.reward.giftNeedAmount))
        return
      }
      points = Math.floor(Number(raw))
      if (points === 0 && !confirmed) {
        setZeroCode(code)
        return
      }
    }
    try {
      await campaignApi.setGift(code, points)
      toast.ok(reset ? t(G.reward.giftRestored) : points === 0 ? t(G.reward.giftStoppedToast) : t(G.reward.giftSaved, { n: points ?? 0 }))
      gifts.reload()
    } catch (e) {
      toast.error(errorTextC(e, locale))
    } finally {
      setZeroCode(null)
    }
  }

  const openRule = (r: RewardRule | 'new') => {
    setForm(
      r === 'new'
        ? EMPTY_RULE
        : {
            name: r.name,
            modelPattern: r.model_pattern,
            threshold: String(r.threshold_tokens),
            type: r.reward_type,
            ref: r.reward_ref,
            startAt: toLocalInput(r.start_at),
            enabled: r.enabled,
          },
    )
    setEditing(r)
  }

  const saveRule = async () => {
    if (!editing) return
    const payload: RewardRuleInput = {
      name: form.name.trim(),
      model_pattern: form.modelPattern.trim(),
      threshold_tokens: parseInt(form.threshold, 10) || 0,
      reward_type: form.type,
      reward_ref: form.ref.trim(),
      start_at: fromLocalInput(form.startAt),
      enabled: form.type === 'badge' ? false : form.enabled,
    }
    setBusy(true)
    try {
      if (editing === 'new') {
        await campaignApi.createRule(payload)
        toast.ok(t(G.reward.created))
      } else {
        await campaignApi.updateRule(editing.id, editing.revision, payload)
        toast.ok(t(G.reward.updated))
      }
      setEditing(null)
      rules.reload()
    } catch (e) {
      toast.error(errorTextC(e, locale))
      if (e && typeof e === 'object' && 'status' in e && (e as { status: number }).status === 409) {
        setEditing(null)
        rules.reload()
      }
    } finally {
      setBusy(false)
    }
  }

  const retry = async (id: number) => {
    try {
      const r = await campaignApi.retryGrant(id)
      const s = r.grant.status
      toast.ok(t(G.reward.retried, { s: s in G.reward.grantStatus ? t(G.reward.grantStatus[s]) : s }))
      grants.reload()
      rules.reload()
    } catch (e) {
      toast.error(errorTextC(e, locale))
    }
  }

  const stat = gifts.data?.signup
  const ruleList = rules.data?.rules ?? []
  const grantList = grants.data?.grants ?? []
  const typeLabel = (type: string) => (type in G.reward.rewardTypes ? t(G.reward.rewardTypes[type]) : type)

  return (
    <OpsPage
      eyebrow={t(G.reward.eyebrow)}
      title={t(G.reward.title)}
      desc={t(G.reward.desc)}
      chip={t(G.reward.chip)}
    >
      <OpsNote>{t(G.reward.note)}</OpsNote>

      <section className="ops-section-card" style={{ marginBottom: 18 }}>
        <div className="ops-section-head">
          <div>
            <div className="ops-section-title">{t(G.reward.giftsTitle)}</div>
            <div className="ops-section-copy">{t(G.reward.giftsCopy)}</div>
          </div>
          <div className="ops-head-chip">
            {stat ? t(G.reward.giftStat, { n: fmtNum(stat.grants, tag), m: fmtNum(stat.grants_today, tag), c: fmtNum(stat.cost_petals, tag) }) : t(G.common.dash)}
          </div>
        </div>
        <div className="table-wrap" style={{ border: 0, borderRadius: 0 }}>
          <div className="table-inner">
            <table>
              <thead>
                <tr>
                  <th>{t(G.reward.giftCols.gift)}</th>
                  <th>{t(G.reward.giftCols.desc)}</th>
                  <th>{t(G.reward.giftCols.amount)}</th>
                  <th>{t(G.reward.giftCols.status)}</th>
                </tr>
              </thead>
              <tbody>
                {!gifts.data ? (
                  <tr>
                    <td colSpan={4} className="empty">
                      {gifts.error ? errorTextC(gifts.error, locale) : t(G.common.loading)}
                    </td>
                  </tr>
                ) : gifts.data.gifts.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="empty">
                      {t(G.reward.giftEmpty)}
                    </td>
                  </tr>
                ) : (
                  gifts.data.gifts.map((g) => (
                    <tr key={g.code}>
                      <td>
                        <div style={{ fontWeight: 500 }}>{giftLabel(g)}</div>
                        <div className="mono" style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>
                          {g.code}
                        </div>
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--text-secondary)', maxWidth: 340 }}>{giftDesc(g)}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <input
                            type="number"
                            min={0}
                            step={1}
                            max={gifts.data?.max_points}
                            value={amounts[g.code] ?? ''}
                            onChange={(e) => setAmounts((a) => ({ ...a, [g.code]: e.target.value }))}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.nativeEvent.isComposing) void saveGift(g.code, false)
                            }}
                            style={{ width: 72, padding: '4px 6px', fontSize: 12, fontFamily: 'var(--mono)', background: 'var(--bg-input)', border: '1px solid var(--border-medium)', borderRadius: 5, color: 'var(--text-primary)', textAlign: 'right' }}
                          />
                          <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{t(G.common.petals)}</span>
                          <button className="btn btn-secondary btn-sm" type="button" onClick={() => void saveGift(g.code, false)}>
                            {t(G.reward.giftSave)}
                          </button>
                        </div>
                      </td>
                      <td style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                        {g.points === 0 && <span className="badge badge-off">{t(G.reward.giftStopped)}</span>}{' '}
                        {g.is_custom ? (
                          <>
                            {t(G.reward.giftCustom, { n: fmtNum(g.default_points, tag) })}
                            <a href="#restore" onClick={(e) => { e.preventDefault(); void saveGift(g.code, true) }} style={{ color: 'var(--primary)' }}>
                              {t(G.reward.giftRestore)}
                            </a>
                          </>
                        ) : (
                          t(G.reward.giftDefault)
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="ops-section-card">
        <div className="ops-section-head">
          <div>
            <div className="ops-section-title">{t(G.reward.rulesTitle)}</div>
            <div className="ops-section-copy">{t(G.reward.rulesCopy)}</div>
          </div>
          <button className="btn btn-primary btn-sm" type="button" onClick={() => openRule('new')}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>{' '}
            {t(G.reward.newRule)}
          </button>
        </div>
        <div className="table-wrap" style={{ border: 0, borderRadius: 0 }}>
          <div className="table-inner">
            <table>
              <thead>
                <tr>
                  <th>{t(G.reward.ruleCols.id)}</th>
                  <th>{t(G.reward.ruleCols.name)}</th>
                  <th>{t(G.reward.ruleCols.status)}</th>
                  <th>{t(G.reward.ruleCols.model)}</th>
                  <th>{t(G.reward.ruleCols.threshold)}</th>
                  <th>{t(G.reward.ruleCols.reward)}</th>
                  <th>{t(G.reward.ruleCols.start)}</th>
                  <th>{t(G.reward.ruleCols.granted)}</th>
                  <th>{t(G.reward.ruleCols.failed)}</th>
                  <th>{t(G.reward.ruleCols.actions)}</th>
                </tr>
              </thead>
              <tbody>
                {!rules.data ? (
                  <tr>
                    <td colSpan={10} className="empty">
                      {rules.error ? errorTextC(rules.error, locale) : t(G.common.loading)}
                    </td>
                  </tr>
                ) : ruleList.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="empty">
                      {t(G.reward.rulesEmpty)}
                    </td>
                  </tr>
                ) : (
                  ruleList.map((r) => (
                    <tr key={r.id}>
                      <td>{r.id}</td>
                      <td>{r.name}</td>
                      <td>
                        {r.enabled ? (
                          <span style={{ color: 'var(--success-text)', fontWeight: 600 }}>{t(G.reward.ruleEnabled)}</span>
                        ) : (
                          <span style={{ color: 'var(--text-tertiary)' }}>{t(G.reward.ruleDisabled)}</span>
                        )}
                      </td>
                      <td>{r.model_pattern || <span style={{ color: 'var(--text-tertiary)' }}>{t(G.reward.allModels)}</span>}</td>
                      <td>
                        {fmtNum(r.threshold_tokens, tag)}（{fmtTokens(r.threshold_tokens)}）
                      </td>
                      <td>
                        {typeLabel(r.reward_type)} {r.reward_ref ? <code style={codeStyle}>{r.reward_ref}</code> : t(G.common.dash)}
                      </td>
                      <td>{fmtDateTime(r.start_at, locale)}</td>
                      <td>
                        {fmtNum(r.granted_count, tag)}
                        {r.delivered_count ? t(G.reward.delivered, { n: fmtNum(r.delivered_count, tag) }) : ''}
                      </td>
                      <td>{r.failed_count ? <span style={{ color: 'var(--error-text)' }}>{fmtNum(r.failed_count, tag)}</span> : '0'}</td>
                      <td>
                        <button className="btn btn-secondary btn-sm" type="button" onClick={() => openRule(r)}>
                          {t(G.reward.edit)}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="ops-section-card" style={{ marginTop: 18 }}>
        <div className="ops-section-head">
          <div>
            <div className="ops-section-title">{t(G.reward.grantsTitle)}</div>
            <div className="ops-section-copy">{t(G.reward.grantsCopy)}</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <ToolSelect
              value={grantRule}
              onChange={(e) => setGrantRule(e.target.value)}
              options={[{ value: '', label: t(G.reward.allRules) }, ...ruleList.map((r) => ({ value: String(r.id), label: `#${r.id} ${r.name}` }))]}
              style={{ fontSize: 12 }}
            />
            <ToolSelect
              value={grantStatus}
              onChange={(e) => setGrantStatus(e.target.value)}
              options={[
                { value: '', label: t(G.reward.allStatus) },
                ...(['pending', 'delivered', 'owned', 'failed'] as const).map((s) => ({ value: s, label: t(G.reward.grantStatus[s]) })),
              ]}
              style={{ fontSize: 12 }}
            />
          </div>
        </div>
        <div className="table-wrap" style={{ border: 0, borderRadius: 0 }}>
          <div className="table-inner">
            <table>
              <thead>
                <tr>
                  <th>{t(G.reward.grantCols.time)}</th>
                  <th>{t(G.reward.grantCols.user)}</th>
                  <th>{t(G.reward.grantCols.rule)}</th>
                  <th>{t(G.reward.grantCols.reward)}</th>
                  <th>{t(G.reward.grantCols.status)}</th>
                  <th>{t(G.reward.grantCols.attempts)}</th>
                  <th>{t(G.reward.grantCols.error)}</th>
                  <th>{t(G.reward.grantCols.actions)}</th>
                </tr>
              </thead>
              <tbody>
                {!grants.data ? (
                  <tr>
                    <td colSpan={8} className="empty">
                      {grants.error ? errorTextC(grants.error, locale) : t(G.common.loading)}
                    </td>
                  </tr>
                ) : grantList.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="empty">
                      {t(G.reward.grantsEmpty)}
                    </td>
                  </tr>
                ) : (
                  grantList.map((g) => (
                    <tr key={g.id}>
                      <td>{fmtDateTime(g.created_at, locale)}</td>
                      <td style={{ cursor: 'pointer' }} onClick={() => navigate(`/accounts/${g.account_id}`)}>
                        {g.account_name}
                        <div style={{ fontSize: 11, color: 'var(--text-tertiary)', fontFamily: 'var(--mono)' }}>{g.account_id}</div>
                      </td>
                      <td>
                        #{g.rule_id} {g.rule_name}
                      </td>
                      <td>
                        {typeLabel(g.reward_type)} <code style={codeStyle}>{g.reward_ref}</code>
                      </td>
                      <td>
                        <span style={{ color: STATUS_COLOR[g.status] ?? 'var(--text-secondary)', fontWeight: 600 }}>
                          {g.status in G.reward.grantStatus ? t(G.reward.grantStatus[g.status]) : g.status}
                        </span>
                      </td>
                      <td>{g.attempts}</td>
                      <td style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={g.last_error}>
                        {g.last_error || t(G.common.dash)}
                      </td>
                      <td>
                        {g.status === 'failed' && (
                          <button className="btn btn-secondary btn-sm" type="button" onClick={() => void retry(g.id)}>
                            {t(G.reward.retry)}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <AliceModal
        open={editing !== null}
        title={editing && editing !== 'new' ? t(G.reward.modalEdit, { id: editing.id }) : t(G.reward.modalNew)}
        busy={busy}
        onClose={() => setEditing(null)}
        footer={
          <>
            <button className="btn btn-secondary" type="button" disabled={busy} onClick={() => setEditing(null)}>
              {t(G.common.cancel)}
            </button>
            <button className="btn btn-primary" type="button" disabled={busy} onClick={() => void saveRule()}>
              {t(G.reward.f.save)}
            </button>
          </>
        }
      >
        <div className="form-group">
          <label>{t(G.reward.f.name)}</label>
          <input maxLength={60} placeholder={t(G.reward.f.namePh)} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>
        <div className="grid-2">
          <div className="form-group">
            <label>{t(G.reward.f.model)}</label>
            <input maxLength={100} placeholder={t(G.reward.f.modelPh)} value={form.modelPattern} onChange={(e) => setForm({ ...form, modelPattern: e.target.value })} />
          </div>
          <div className="form-group">
            <label>{t(G.reward.f.threshold)}</label>
            <input type="number" min={1} step={1} value={form.threshold} onChange={(e) => setForm({ ...form, threshold: e.target.value })} />
          </div>
        </div>
        <div className="grid-2">
          <div className="form-group">
            <label>{t(G.reward.f.type)}</label>
            <select
              value={form.type}
              onChange={(e) => {
                const type = e.target.value as RewardType
                setForm({ ...form, type, enabled: type === 'badge' ? false : form.enabled })
              }}
            >
              <option value="bonus">{t(G.reward.f.typeBonus)}</option>
              <option value="badge">{t(G.reward.f.typeBadge)}</option>
            </select>
          </div>
          <div className="form-group">
            <label>{t(form.type === 'bonus' ? G.reward.f.refBonus : G.reward.f.refBadge)}</label>
            <input
              maxLength={128}
              placeholder={t(form.type === 'bonus' ? G.reward.f.refBonusPh : G.reward.f.refBadgePh)}
              value={form.ref}
              onChange={(e) => setForm({ ...form, ref: e.target.value })}
              style={{ fontFamily: 'var(--mono)', fontSize: 13 }}
            />
          </div>
        </div>
        <div className="form-group">
          <label>{t(G.reward.f.start)}</label>
          <input type="datetime-local" value={form.startAt} onChange={(e) => setForm({ ...form, startAt: e.target.value })} />
        </div>
        <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            id="ur-enabled"
            type="checkbox"
            style={{ width: 'auto' }}
            checked={form.type === 'badge' ? false : form.enabled}
            disabled={form.type === 'badge'}
            onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
          />
          <label htmlFor="ur-enabled" style={{ margin: 0, cursor: 'pointer' }}>
            {t(G.reward.f.enabled)}
          </label>
        </div>
      </AliceModal>

      <ConfirmModal
        open={zeroCode !== null}
        title={t(G.reward.giftZeroTitle)}
        danger
        onConfirm={() => zeroCode && void saveGift(zeroCode, false, true)}
        onClose={() => setZeroCode(null)}
      >
        {t(G.reward.giftZeroConfirm)}
      </ConfirmModal>
    </OpsPage>
  )
}
